import {
  HHButton,
  HHStack,
  HHTextField,
} from '@hinge-health/react-component-library';
import Autocomplete from '@mui/material/Autocomplete';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  ProcedureCode,
  useGetAllClientsQuery,
  useGetAllInsurersQuery,
  useGetPartnershipsQuery,
  useGetPaymentTypesQuery,
  useGetProcedureCodesLazyQuery,
} from '../../types';
import { fetchAllPages } from '../../utils/fetch-all-pages';

// ACCOUNTS-AND-PLAN-MIGRATION: partnershipId, insurerId, clientId are client/insurer/partnership-scoped, not resolvable via contract_id — will be addressed by the Accounts & Plan migration.
export interface ProcedureCodeFilterParams {
  partnershipId: number | null;
  insurerId: number | null;
  clientId: number | null;
  code: string;
  paymentType: string;
}

export const defaultProcedureCodeFilters: ProcedureCodeFilterParams = {
  partnershipId: null,
  insurerId: null,
  clientId: null,
  code: '',
  paymentType: '',
};

interface PaymentTypeOption {
  code: string;
  name: string;
}

interface FilterFormValues {
  partnership: { id: number; name: string } | null;
  insurer: { id: number; name: string } | null;
  client: { id: number; name: string } | null;
  code: string | null;
  paymentType: PaymentTypeOption | null;
}

const AUTOCOMPLETE_SX = {
  flex: '1 1 160px',
  minWidth: 140,
} as const;

interface ProcedureCodeFilterBarProps {
  filterParams: ProcedureCodeFilterParams;
  setFilterParams: Dispatch<SetStateAction<ProcedureCodeFilterParams>>;
  onFiltersApplied: () => void;
}

export const ProcedureCodeFilterBar = ({
  filterParams,
  setFilterParams,
  onFiltersApplied,
}: ProcedureCodeFilterBarProps): JSX.Element => {
  const { data: partnershipsData, loading: partnershipsLoading } =
    useGetPartnershipsQuery({ variables: { type: 'billing' } });

  const { data: insurersData, loading: insurersLoading } =
    useGetAllInsurersQuery();

  const { data: clientsData, loading: clientsLoading } =
    useGetAllClientsQuery();

  const { data: paymentTypesData, loading: paymentTypesLoading } =
    useGetPaymentTypesQuery();

  const [getProcedureCodes] = useGetProcedureCodesLazyQuery();
  const [cptCodeOptions, setCptCodeOptions] = useState<string[]>([]);
  const [cptCodesLoading, setCptCodesLoading] = useState(true);

  useEffect(() => {
    const loadAllCptCodes = async (): Promise<void> => {
      setCptCodesLoading(true);
      const allItems = await fetchAllPages<ProcedureCode>(
        async (page, pageSize) => {
          const result = await getProcedureCodes({
            variables: { page, size: pageSize },
          });
          const data = result.data?.getProcedureCodes;
          if (!data) return undefined;
          return { items: data.items, total: data.total ?? 0 };
        },
      );

      const uniqueCodes = [...new Set(allItems.map(item => item.cptCode))].sort(
        (a, b) => a.localeCompare(b),
      );
      setCptCodeOptions(uniqueCodes);
      setCptCodesLoading(false);
    };

    loadAllCptCodes();
  }, [getProcedureCodes]);

  const partnershipOptions: { id: number; name: string }[] = useMemo(
    () =>
      (partnershipsData?.getPartnerships ?? [])
        .map(p => ({ id: p.id, name: p.name }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [partnershipsData],
  );

  const insurerOptions: { id: number; name: string }[] = useMemo(
    () =>
      (insurersData?.getAllInsurers ?? [])
        .map(i => ({ id: i.id, name: i.name }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [insurersData],
  );

  const clientOptions: { id: number; name: string }[] = useMemo(
    () =>
      (clientsData?.getAllClients ?? [])
        .map(c => ({ id: c.id, name: c.name }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [clientsData],
  );

  const paymentTypeOptions: PaymentTypeOption[] = useMemo(
    () =>
      (paymentTypesData?.getPaymentTypes ?? [])
        .map(pt => ({ code: pt.code, name: pt.name }))
        .sort((a, b) => a.code.localeCompare(b.code)),
    [paymentTypesData],
  );

  const toFormValues = useCallback(
    (params: ProcedureCodeFilterParams): FilterFormValues => ({
      partnership:
        partnershipOptions.find(p => p.id === params.partnershipId) ?? null,
      insurer: insurerOptions.find(i => i.id === params.insurerId) ?? null,
      client: clientOptions.find(c => c.id === params.clientId) ?? null,
      code: params.code || null,
      paymentType:
        paymentTypeOptions.find(pt => pt.name === params.paymentType) ?? null,
    }),
    [partnershipOptions, insurerOptions, clientOptions, paymentTypeOptions],
  );

  const { handleSubmit, control, formState, reset } = useForm<FilterFormValues>(
    {
      mode: 'onChange',
      defaultValues: {
        partnership: null,
        insurer: null,
        client: null,
        code: null,
        paymentType: null,
      },
    },
  );

  const { isDirty } = formState;

  useEffect(() => {
    reset(toFormValues(filterParams));
  }, [filterParams, toFormValues, reset]);

  const onApplyClick = (): void => {
    handleSubmit(data => {
      const formatted: ProcedureCodeFilterParams = {
        partnershipId: data.partnership?.id ?? null,
        insurerId: data.insurer?.id ?? null,
        clientId: data.client?.id ?? null,
        code: data.code ?? '',
        paymentType: data.paymentType?.name ?? '',
      };
      setFilterParams(formatted);
      onFiltersApplied();
      reset(data);
    })();
  };

  const handleReset = (): void => {
    reset({
      partnership: null,
      insurer: null,
      client: null,
      code: null,
      paymentType: null,
    });
    setFilterParams(defaultProcedureCodeFilters);
    onFiltersApplied();
  };

  const isFiltersDefault =
    filterParams.partnershipId === null &&
    filterParams.insurerId === null &&
    filterParams.clientId === null &&
    filterParams.code === '' &&
    filterParams.paymentType === '';

  return (
    <HHStack
      direction="row"
      alignItems="flex-end"
      sx={{ gap: 2, paddingRight: 6 }}
    >
      <HHStack
        direction="row"
        alignItems="flex-end"
        flexWrap="wrap"
        sx={{ gap: 2, flex: 1, minWidth: 0 }}
      >
        <Controller
          control={control}
          name="partnership"
          render={({ field: { onChange, value } }): JSX.Element => (
            <Autocomplete
              id="partnership-filter"
              data-testid="partnership-filter"
              value={value}
              options={partnershipOptions}
              size="small"
              sx={AUTOCOMPLETE_SX}
              loading={partnershipsLoading}
              onChange={(_, newValue): void => onChange(newValue)}
              isOptionEqualToValue={(option, val): boolean =>
                option.id === val.id
              }
              getOptionLabel={(option): string => option.name}
              renderInput={(params): JSX.Element => (
                <HHTextField
                  {...params}
                  hhVariant="variant-bypass"
                  variant="standard"
                  label="Partnership"
                />
              )}
            />
          )}
        />

        <Controller
          control={control}
          name="insurer"
          render={({ field: { onChange, value } }): JSX.Element => (
            <Autocomplete
              id="insurer-filter"
              data-testid="insurer-filter"
              value={value}
              options={insurerOptions}
              size="small"
              sx={AUTOCOMPLETE_SX}
              loading={insurersLoading}
              onChange={(_, newValue): void => onChange(newValue)}
              isOptionEqualToValue={(option, val): boolean =>
                option.id === val.id
              }
              getOptionLabel={(option): string => option.name}
              renderInput={(params): JSX.Element => (
                <HHTextField
                  {...params}
                  hhVariant="variant-bypass"
                  variant="standard"
                  label="Insurer"
                />
              )}
            />
          )}
        />

        <Controller
          control={control}
          name="client"
          render={({ field: { onChange, value } }): JSX.Element => (
            <Autocomplete
              id="client-filter"
              data-testid="client-filter"
              value={value}
              options={clientOptions}
              size="small"
              sx={AUTOCOMPLETE_SX}
              loading={clientsLoading}
              onChange={(_, newValue): void => onChange(newValue)}
              isOptionEqualToValue={(option, val): boolean =>
                option.id === val.id
              }
              getOptionLabel={(option): string => option.name}
              renderInput={(params): JSX.Element => (
                <HHTextField
                  {...params}
                  hhVariant="variant-bypass"
                  variant="standard"
                  label="Client"
                />
              )}
            />
          )}
        />

        <Controller
          control={control}
          name="code"
          render={({ field: { onChange, value } }): JSX.Element => (
            <Autocomplete
              id="cpt-code-filter"
              data-testid="cpt-code-filter"
              value={value}
              options={cptCodeOptions}
              size="small"
              sx={AUTOCOMPLETE_SX}
              loading={cptCodesLoading}
              onChange={(_, newValue): void => onChange(newValue)}
              renderInput={(params): JSX.Element => (
                <HHTextField
                  {...params}
                  hhVariant="variant-bypass"
                  variant="standard"
                  label="CPT Code"
                />
              )}
            />
          )}
        />

        <Controller
          control={control}
          name="paymentType"
          render={({ field: { onChange, value } }): JSX.Element => (
            <Autocomplete
              id="payment-type-filter"
              data-testid="payment-type-filter"
              value={value}
              options={paymentTypeOptions}
              size="small"
              sx={AUTOCOMPLETE_SX}
              loading={paymentTypesLoading}
              onChange={(_, newValue): void => onChange(newValue)}
              isOptionEqualToValue={(option, val): boolean =>
                option.code === val.code
              }
              getOptionLabel={(option): string => option.code}
              renderInput={(params): JSX.Element => (
                <HHTextField
                  {...params}
                  hhVariant="variant-bypass"
                  variant="standard"
                  label="Payment Type"
                />
              )}
            />
          )}
        />
      </HHStack>

      <HHStack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ flexShrink: 0 }}
      >
        <HHButton
          size="medium"
          hhVariant="contained"
          onClick={onApplyClick}
          disabled={!isDirty}
          data-testid="apply-filters-button"
        >
          Apply
        </HHButton>
        <HHButton
          size="medium"
          hhVariant="outlined"
          onClick={handleReset}
          disabled={isFiltersDefault && !isDirty}
          data-testid="reset-filters-button"
        >
          Reset
        </HHButton>
      </HHStack>
    </HHStack>
  );
};
